import React, {useEffect, useState, useRef} from 'react';
import { Button } from '../ui/button';
import { Thermometer, X } from 'lucide-react';
import { Slider } from '../ui/slider';
import { CHAT_CONFIG } from '@/constants/chat.constant';

/**
 * Props for the TemperatureDropdown component
 */
interface TemperatureDropdownProps {
    /** Current temperature value (0.0 to 2.0) */
    temperature: number;
    /** Callback function called when temperature changes */
    onTemperatureChange: (value: number) => void;
}

/**
 * TemperatureDropdown component displays a dropdown button with a slider to control
 * the temperature/creativity level for AI responses. Lower values are more deterministic,
 * higher values are more creative. Range is typically 0.0 to 2.0.
 * 
 * @component
 * @param {TemperatureDropdownProps} props - Component props
 * @param {number} props.temperature - Current temperature value
 * @param {(value: number) => void} props.onTemperatureChange - Callback fired when temperature changes
 * @returns {JSX.Element} Button with dropdown containing temperature slider
 * 
 * @example
 * ```tsx
 * <TemperatureDropdown 
 *   temperature={0.7} 
 *   onTemperatureChange={setTemperature}
 * />
 * ```
 */
export const TemperatureDropdown :React.FC<TemperatureDropdownProps> = ({
  temperature,
  onTemperatureChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sliderValue, setSliderValue] = useState(temperature);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setSliderValue(temperature);
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSliderChange = (value: number[]) => {
    const newValue = value[0];
    setSliderValue(newValue);
    onTemperatureChange(newValue);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant={isOpen ? "default" : "outline"}
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Temperature settings"
        aria-expanded={isOpen}
        className={`rounded-full ${
          isOpen
            ? "bg-[#005587] hover:bg-[#004070] text-white"
            : "border-[#D4D4D8] text-[#005587] hover:text-[#005587] hover:bg-gray-50"
        }`}
      >
        <Thermometer size={16} />
      </Button>

      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-[#09090B] text-sm">
                Temperature settings
              </p>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                aria-label="Close temperature settings"
                className="h-6 w-6 rounded-full hover:bg-gray-100"
              >
                <X size={14} className="text-gray-500" />
              </Button>
            </div>

            <p className="text-[#005587] font-medium text-sm">
              Temperature: {sliderValue.toFixed(1)}
            </p>

            <div className="space-y-3">
              <Slider
                value={[sliderValue]}
                onValueChange={handleSliderChange}
                min={CHAT_CONFIG.TEMPERATURE.MIN}
                max={CHAT_CONFIG.TEMPERATURE.MAX}
                step={CHAT_CONFIG.TEMPERATURE.STEP}
                className="w-full cursor-pointer"
                aria-label="Temperature slider"
              />
              <div className="flex justify-between text-xs text-[#71717B]">
                <span>Deterministic</span>
                <span>Creative</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};